/* eslint-disable no-prototype-builtins */
import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'

import behaviors from '../../../util/behaviors'
import Lurker from '../../../util/lurker'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import passwordlessSendEmailApiCall from '../../../api/passwordlessSendEmailApiCall'
import transientErrorsStorage from '../../../util/transientErrorsStorage'
import EmailFormBaseTemplate from '../EmailFormBaseTemplate'

import HRule from '../../atoms/HRule'
import TextCallout from '../../molecules/TextCallout'
import ScrollView from '../../atoms/ScrollView'
import Paper from '../../atoms/Paper'
import LogoTitle from '../../molecules/LogoTitle'
import TermsAndPrivacyNotice from '../../molecules/TermsAndPrivacyNotice'
import medias from '../../../util/medias'

const lurker = new Lurker('login')

const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0px;
  margin-bottom: 16px;
  margin-left: 0px;
`

const Content = styled(View)`
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  margin: auto;
  margin-top: 64px;
  margin-bottom: 64px;
  max-width: 466px;
  ${medias.medium`
    margin: 0;
    margin-top: 4px;
    margin-bottom: 0;
  `}
`

const HRuleWrapper = styled(View)`
  margin-top: 24px;
  margin-right: 0;
  margin-bottom: 24px;
  margin-left: 0;
`

const FooterWrapper = styled(View)`
  display: flex;
  margin-top: 24px;
`

const Title = styled.span.attrs({ className: 'typo-body-large' })`
  text-align: center;
  font-size: var(--font-size-3);
  margin-top: var(--spacing-3);
  font-weight: var(--font-weight-semibold);
`

const PaperWrapper = styled(Paper)`
  border: 1px solid var(--color-neutral-90);
`

const errors = {
  showInactiveError: false,
  showInvalidEmailError: false,
  showGenericError: false,
  showRateLimitError: false,
  showAccountNotFoundError: false,
  showExpiredTokenError: false,
}

class MagicLinkTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ...errors,
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt('login_magiclink')
    this.checkTransientErrors()
  }

  onLinkSent = email => {
    const { onSuccess } = this.props
    onSuccess(email)
  }

  onApiCallError = (error = {}) => {
    if (this.props.onError) {
      this.props.onError()
    }
    const { response = {} } = error
    const prefix = 'magiclink_'
    if (response.status === 401) {
      this.onUnauthorizedError(response)
    } else if (response.status === 429) {
      lurker.viewMessageEvt('error', {
        accounts_reason: `${prefix}rate_limit_exceeded`,
        accounts_status_code: response.status,
      })
      this.showError('showRateLimitError')
    } else if (response.status === 410) {
      lurker.viewMessageEvt('error', {
        accounts_reason: `${prefix}account_inactive`,
        accounts_status_code: response.status,
      })
      this.showError('showInactiveError')
    } else if (response.status === 403) {
      lurker.viewMessageEvt('error', {
        accounts_reason: `${prefix}recaptcha_invalid_token`,
        accounts_status_code: response.status,
      })
      this.showError('showGenericError')
    } else if (response.status === 400) {
      if (response.data && response.data === 'Missing recaptcha response token') {
        lurker.viewMessageEvt('error', {
          accounts_reason: `${prefix}recaptcha_missing_token`,
          accounts_status_code: response.status,
        })
        this.showError('showGenericError')
      }
    } else if (response.status) {
      lurker.viewMessageEvt('error', {
        accounts_reason: `${prefix}unexpected_status_code`,
        accounts_status_code: response.status,
      })
      this.showError('showGenericError')
    } else {
      lurker.viewMessageEvt('error', {
        accounts_reason: `${prefix}unexpected_error`,
        accounts_error_message: (error && error.message) || 'unknown',
      })
      this.showError('showGenericError')
    }
    behaviors.scrollToTop()
  }

  sendMagicLink = email =>
    passwordlessSendEmailApiCall(email)
      .then(() => this.onLinkSent(email))
      .catch(e => this.onApiCallError(e))

  showError = (error, extra) => {
    this.setState({ ...errors, ...extra, [error]: true })
  }

  checkTransientErrors() {
    transientErrorsStorage.get().then(error => {
      if (typeof error === 'string') {
        this.setState({ [error]: true })
      }
    })
  }

  renderError = () => {
    const { onRegisterClick } = this.props
    const { showGenericError, showInactiveError, showAccountNotFoundError, showExpiredTokenError } =
      this.state
    return (
      <React.Fragment>
        {showGenericError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível acessar sua conta. Por favor, tente novamente." />
          </ErrorMsgWrapper>
        )}
        {showInactiveError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs
              contactUsLink="https://ajuda.olx.com.br/s/article/reativar-conta-na-olx"
              text="Este e-mail não pode mais ser usado."
            />
          </ErrorMsgWrapper>
        )}
        {showAccountNotFoundError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showAccountNotFoundError
              errorAction={onRegisterClick}
              text="Não existe uma conta com este e-mail."
            />
          </ErrorMsgWrapper>
        )}
        {showExpiredTokenError && (
          <ErrorMsgWrapper>
            <GenericErrorBox showContactUs={false} text="O link para acesso rápido expirou." />
          </ErrorMsgWrapper>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { showInactiveError } = this.state
    const { onClickBackToLogin, abTests } = this.props

    return (
      <ScrollView>
        <Content>
          <PaperWrapper>
            <LogoTitle>
              <Title>Entre de forma rápida e segura</Title>
            </LogoTitle>

            <EmailFormBaseTemplate
              buttonText="Receber acesso por e-mail"
              hasError={showInactiveError}
              lurker={lurker}
              onSubmit={this.sendMagicLink}
              renderError={this.renderError}
              renderTitle="Enviaremos um código único para seu e-mail. Use-o para entrar com segurança."
              submitEventName="magiclink"
              useRecaptcha={false}
            />
            <HRuleWrapper>
              <HRule />
            </HRuleWrapper>
            <TextCallout
              linkText="Escolher outra opção para entrar"
              href="/acesso"
              onClick={onClickBackToLogin}
            />
          </PaperWrapper>
          <FooterWrapper>
            <TermsAndPrivacyNotice />
          </FooterWrapper>
        </Content>
      </ScrollView>
    )
  }
}

MagicLinkTemplate.propTypes = {
  onError: PropTypes.func,
  onRegisterClick: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onClickBackToLogin: PropTypes.func,
}

export default MagicLinkTemplate
